import { useEffect, useState, type ReactNode } from "react";
import { ApiError } from "../lib/api";
import type { WorkloadResult } from "../lib/types";
import { Icon } from "./icons";
import { Modal } from "./Modal";
import { OtpCells } from "./OtpCells";
import { Alert, Button, Field, Input, TypedPhrase, cx } from "./ui";

/**
 * The one dialog every operator delete goes through — a single app, the rows
 * ticked on a tenant, everything a suspended tenant has, or a page of them.
 *
 * Three screens, in the order the decision is actually made.
 *
 * **The manifest.** What is about to go, as a ledger: one numbered row per
 * item, kind, name, and the one fact that distinguishes it — the domain an
 * app serves, the port a database is published on. An operator who ticked the
 * wrong row finds out here, reading a list, not later, reading a result. Under
 * it, what "deleted" means for each of the three things a workload is made
 * of, as three short columns rather than a paragraph, because a paragraph
 * about consequences is skimmed and a table is read.
 *
 * **The confirmation.** Two things typed: the organization's name, and the
 * six digits from the operator's own authenticator. The session already
 * proved the authenticator at sign-in; asking again on the request that
 * destroys a tenant means a console left open or a token lifted from a laptop
 * cannot delete anything by itself. The code is entered into six cells so a
 * mistyped digit is visible as one wrong cell rather than one wrong string.
 * `force` sits behind a disclosure: it is the answer to one situation — an
 * engine that will not answer — and putting it on the main path made every
 * delete look like it had a second setting to decide.
 *
 * **The result.** The API answers 200 whatever happened to the individual
 * rows, because a bulk delete that raised on its first refusal would hide the
 * rest. So the dialog tallies and then repeats the manifest with an outcome
 * against each row: gone, gone with something left on the engine or in the
 * zone, or refused and why. An operator who closed a spinner and saw a
 * shorter list would not know which.
 */

export interface ManifestItem {
  kind: "app" | "database" | "organization";
  id: string;
  name: string;
  /** The one fact that tells it apart — a domain, an engine and port, a region. */
  detail?: string;
  /** A short state worth a second look — "suspended", "published". */
  flag?: string;
}

type Phase = "review" | "confirm";

export function DeleteWorkloadsModal({
  open,
  onClose,
  title,
  description,
  items,
  confirmName,
  confirmLabel = "Delete",
  noun = "item",
  run,
  pending,
  results,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** What is about to go, one row each. */
  items: ManifestItem[];
  /** What must be typed, usually the organization's name. */
  confirmName: string;
  confirmLabel?: string;
  /** What a row is, for the counts — "item", "organization". */
  noun?: string;
  /** Sends the request; the caller owns the mutation and its result. */
  run: (opts: { force: boolean; otp: string }) => Promise<unknown>;
  pending: boolean;
  /** Filled by the caller once the request answers; the dialog then reports. */
  results: WorkloadResult[] | null;
  /** Closing after results were shown, which is when lists should refresh. */
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("review");
  const [typed, setTyped] = useState("");
  const [otp, setOtp] = useState("");
  const [force, setForce] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = typed.trim() === confirmName && otp.length === 6;
  const count = items.length;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

  function reset() {
    setPhase("review");
    setTyped("");
    setOtp("");
    setForce(false);
    setError(null);
  }

  function close() {
    if (pending) return;
    reset();
    if (results) onDone();
    else onClose();
  }

  async function submit() {
    setError(null);
    try {
      await run({ force, otp });
    } catch (cause) {
      // A refused code is the one error worth clearing the field for: the
      // next code is a different code, and the old one is not worth editing.
      if (cause instanceof ApiError && cause.code === "totp_required") setOtp("");
      setError(cause instanceof ApiError ? cause.message : "The request could not be sent.");
    }
  }

  return (
    <Modal open={open} onClose={close} title={title} description={description} dismissable={!pending}>
      {results ? (
        <Outcome items={items} results={results} onDone={close} />
      ) : pending ? (
        <Working count={count} noun={noun} />
      ) : phase === "review" ? (
        <Review
          items={items}
          noun={noun}
          onCancel={close}
          onContinue={() => setPhase("confirm")}
        />
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) void submit();
          }}
          className="space-y-5"
        >
          {error && <Alert>{error}</Alert>}

          {/* The recap, with the way back. Nobody should have to cancel and
              start over to check what they are about to type a code for. */}
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-fg-muted">
              <span className="font-medium text-fg">{plural(count, noun)}</span> in{" "}
              <span className="text-fg">{confirmName}</span>
            </p>
            <button
              type="button"
              onClick={() => setPhase("review")}
              className="text-xs text-fg-subtle underline underline-offset-2 hover:text-fg"
            >
              Review the list
            </button>
          </div>

          <Field
            label={
              <>
                Type <TypedPhrase>{confirmName}</TypedPhrase> to confirm
              </>
            }
            htmlFor="delete-confirm"
            hint="Exactly as shown, including capitals."
          >
            <Input
              id="delete-confirm"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
          </Field>

          <Field
            label="Authenticator code"
            htmlFor="delete-otp"
            hint="The six digits from your authenticator app, right now. Six wrong codes in fifteen minutes lock this for a while."
          >
            <OtpCells id="delete-otp" value={otp} onChange={setOtp} disabled={pending} />
          </Field>

          {/*
            Off the main path. `force` answers one situation — a server that is
            not answering — and a checkbox on every delete made each one look
            like it carried a second decision.
          */}
          <details className="group rounded-md border border-line">
            <summary className="flex cursor-pointer list-none items-center justify-between px-3.5 py-2.5 text-sm text-fg-muted marker:content-[''] hover:text-fg">
              Is the deployment engine unreachable?
              <Icon
                name="chevron"
                className="size-3.5 text-fg-subtle transition-transform duration-200 group-open:rotate-90"
              />
            </summary>
            <label className="flex items-start gap-3 border-t border-line px-3.5 py-3">
              <input
                type="checkbox"
                checked={force}
                onChange={(event) => setForce(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
              />
              <span className="text-sm">
                <span className="block text-fg">Carry on if the engine refuses</span>
                <span className="block text-fg-subtle">
                  The record goes and whatever the engine would not give up is listed for manual
                  cleanup. Off, a refusal leaves the row in place — the right answer on a healthy
                  server.
                </span>
              </span>
            </label>
          </details>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={close} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" icon="trash" disabled={!ready} loading={pending}>
              {confirmLabel}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------ the manifest */

const KIND_LABEL: Record<ManifestItem["kind"], string> = {
  app: "app",
  database: "db",
  organization: "org",
};

function Review({
  items,
  noun,
  onCancel,
  onContinue,
}: {
  items: ManifestItem[];
  noun: string;
  onCancel: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="space-y-5">
      <Ledger
        caption={`${items.length} ${noun}${items.length === 1 ? "" : "s"} · ${
          items.every((item) => item.kind === "organization")
            ? "every workload destroyed"
            : "will be destroyed"
        }`}
        rows={items.map((item) => ({ item }))}
      />

      {/* What "deleted" means, per thing a workload is made of. Three short
          columns get read; the same facts as a paragraph get skimmed. */}
      <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-md border border-line bg-line text-xs">
        {[
          ["Compute & data", "Destroyed now. Nothing to restore from."],
          ["Domains & DNS", "Records released; custom domains stop resolving."],
          ["Backups & keys", "Dumps gone. Deploy keys and webhooks pulled from the repo."],
        ].map(([label, line]) => (
          <div key={label} className="bg-surface px-3 py-2.5">
            <dt className="micro">{label}</dt>
            <dd className="mt-1 leading-5 text-fg-muted">{line}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs leading-5 text-fg-subtle">
        Immediate — not the customer's week-long closure, and nobody can call it off. Each row is
        written to their audit log as done by an operator, and their owners and admins are emailed
        the list.
      </p>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" variant="danger" onClick={onContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
}

/**
 * One numbered row per item, and the same rows again afterwards with an
 * outcome — the manifest is what was promised and the report is the same
 * list annotated, so an operator can read them against each other.
 */
function Ledger({
  caption,
  rows,
}: {
  caption: string;
  rows: { item: ManifestItem; outcome?: ReactNode; notes?: string[] }[];
}) {
  return (
    <div className="overflow-hidden rounded-md border border-line">
      <div className="flex items-center justify-between border-b border-line bg-raised/60 px-3.5 py-2">
        <span className="micro">Manifest</span>
        <span className="micro">{caption}</span>
      </div>
      <ol className="max-h-64 divide-y divide-line overflow-y-auto">
        {rows.map(({ item, outcome, notes }, index) => (
          <li
            key={`${item.kind}-${item.id}`}
            className="ledger-row px-3.5 py-2.5"
            style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}
          >
            <div className="flex items-center gap-3">
              <span className="w-6 shrink-0 font-mono text-[11px] tabular-nums text-fg-subtle">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="w-8 shrink-0 font-mono text-[10px] tracking-[0.12em] text-fg-subtle uppercase">
                {KIND_LABEL[item.kind]}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-fg">{item.name}</span>
                {item.detail && (
                  <span className="block truncate font-mono text-[11px] text-fg-subtle">
                    {item.detail}
                  </span>
                )}
              </span>
              {outcome ?? (item.flag && <span className="shrink-0 text-xs text-warn">{item.flag}</span>)}
            </div>
            {notes?.map((note) => (
              <p key={note} className="mt-1.5 pl-20 text-xs leading-5 text-fg-subtle">
                {note}
              </p>
            ))}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------- the waiting */

function Working({ count, noun }: { count: number; noun: string }) {
  // The engine is asked once per row, in order, and a struggling server —
  // which a suspended tenant's often is — takes its time answering. The wait
  // is named rather than spun through.
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="space-y-4 py-2">
      <div className="h-1.5 overflow-hidden rounded-full bg-raised">
        <div className="progress-sweep h-full w-1/2 rounded-full bg-gradient-to-r from-transparent via-accent to-transparent" />
      </div>
      <div className="flex items-baseline justify-between">
        <p className="text-sm text-fg">
          Deleting {count} {noun}
          {count === 1 ? "" : "s"}…
        </p>
        <span className="font-mono text-xs tabular-nums text-fg-subtle">{seconds}s</span>
      </div>
      <p className="text-xs leading-5 text-fg-subtle">
        One round trip to the deployment engine per row, in order. A server that is struggling —
        which a suspended tenant's often is — answers slowly. Leave this open; the list of what went
        appears here when it finishes.
      </p>
    </div>
  );
}

/* --------------------------------------------------------------- the report */

function Outcome({
  items,
  results,
  onDone,
}: {
  items: ManifestItem[];
  results: WorkloadResult[];
  onDone: () => void;
}) {
  const gone = results.filter((r) => r.deleted && r.warnings.length === 0).length;
  const leftovers = results.filter((r) => r.deleted && r.warnings.length > 0).length;
  const refused = results.filter((r) => !r.deleted).length;

  // The report repeats the manifest's rows so the two read against each
  // other; a result the manifest did not list (a bulk delete found more than
  // the page showed) is appended rather than dropped.
  const byKey = new Map(items.map((item) => [`${item.kind}-${item.id}`, item]));
  const rows = results.map((result) => {
    const item = byKey.get(`${result.kind}-${result.id}`) ?? {
      kind: result.kind,
      id: result.id,
      name: result.name,
    };
    const outcome = !result.deleted ? (
      <span className="shrink-0 text-xs text-danger">Refused</span>
    ) : result.warnings.length > 0 ? (
      <span className="shrink-0 text-xs text-warn">Deleted · leftovers</span>
    ) : (
      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-mint">
        <Icon name="check" className="size-3" />
        Deleted
      </span>
    );
    return { item, outcome, notes: result.error ? [result.error] : result.warnings };
  });

  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-md border border-line bg-line">
        {[
          ["Deleted", gone, "text-mint"],
          ["Leftovers", leftovers, leftovers > 0 ? "text-warn" : "text-fg-subtle"],
          ["Refused", refused, refused > 0 ? "text-danger" : "text-fg-subtle"],
        ].map(([label, n, tone]) => (
          <div key={label as string} className="bg-surface px-3.5 py-3">
            <dt className="micro">{label}</dt>
            <dd className={cx("mt-1 font-mono text-2xl tabular-nums", tone as string)}>{n as number}</dd>
          </div>
        ))}
      </dl>

      {results.length === 0 ? (
        <Alert tone="info">Nothing matched — the rows may already have been deleted.</Alert>
      ) : (
        <Ledger caption={`${results.length} answered`} rows={rows} />
      )}

      {leftovers + refused > 0 && (
        <p className="text-xs leading-5 text-fg-subtle">
          A refused row is still the tenant's and still billed; retry once the engine answers, or
          carry on past it. Leftovers are the engine's to clean by hand — the note under each row
          says what.
        </p>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}
